/** The source census, from the browser's side (S03 — COL-01, COL-06, EXP-33).
 *
 * The one thing worth stating here rather than in a component: **`state` is a closed set of six and
 * the screen switches on it.** `submitted`, `declared_nil`, `missing`, `pending`, `unknown` and
 * `not_expected` are six different sentences, and the generated type is what stops a seventh being
 * invented in a component or one of the six being quietly folded into another. Collapsing any two
 * is how "we have no data" becomes "the emissions were zero", which is the whole reason the census
 * exists.
 *
 * After declaring nil or retiring, the coverage query is invalidated rather than patched. A census
 * is a comparison against the inventory, and a locally-patched row is a second opinion about what
 * the server thinks — the same class of error as a stale total beside a fresh receipt.
 */

import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { refusalFrom } from "./refusal";
import { api } from "./client";
import type { components } from "./schema";

export type SourceCoverage = components["schemas"]["SourceCoverageOut"];
export type SourceWindow = components["schemas"]["WindowOut"];
export type Completeness = components["schemas"]["CompletenessOut"];
export type Coverage = components["schemas"]["CoverageOut"];

export const sourceKeys = {
  coverage: (start: string, end: string) => ["sources", "coverage", start, end] as const,
};


/** What we expect against what arrived, for a window.
 *
 * Refetched on mount like every other figure. A census is only meaningful beside the total it
 * qualifies, and a stale "3 missing" next to a fresh total is worse than no census at all — it
 * would assert completeness the inventory no longer has.
 */
export function useCoverage(
  periodStart: string | undefined,
  periodEnd: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: sourceKeys.coverage(periodStart ?? "none", periodEnd ?? "none"),
    enabled,
    // **No dates, no request** (`useInventory` says why). `skipToken` rather than `enabled` alone,
    // because it is what makes the `string | undefined` honest: `queryFn` cannot be reached with a
    // date it does not have.
    queryFn:
      periodStart && periodEnd
        ? async (): Promise<Coverage> => {
            const { data, error, response } = await api.GET("/api/v1/sources", {
              params: { query: { period_start: periodStart, period_end: periodEnd } },
            });
            if (error || !data) {
              // **`refusalFrom`, not `new Error(detail.message)`** (DOC-01). Both of this read's
              // refusals carry a code — `backwards_period` and `outside_engagement` — and a bare
              // `Error` dropped it on the floor, so a census narrowed out of an auditor's
              // engagement arrived at the screen as the same object as a failed network call. The
              // remedy, where the route sends one, is joined onto the sentence by the same helper
              // every write here uses.
              throw refusalFrom(response, error, "The expected-bills check could not be read.");
            }
            return data;
          }
        : skipToken,
  });
}

export type SourceLinks = components["schemas"]["SourceLinksOut"];

/** What one source actually produced — **EXP-06**.
 *
 * `enabled` is the point: this is fetched when a reader expands a source, not for every row in the
 * census. Thirty sources would be thirty queries on a screen whose job is a single comparison, and
 * the expansion is the signal that somebody wants the detail.
 */
export function useSourceLinks(
  sourceId: string | null,
  periodStart: string,
  periodEnd: string,
) {
  return useQuery({
    queryKey: [...sourceKeys.coverage(periodStart, periodEnd), "links", sourceId],
    enabled: sourceId !== null,
    queryFn: async (): Promise<SourceLinks> => {
      const { data, error, response } = await api.GET("/api/v1/sources/{source_id}/links", {
        params: {
          path: { source_id: sourceId as string },
          query: { period_start: periodStart, period_end: periodEnd },
        },
      });
      if (error || !data) {
        throw refusalFrom(response, error, "This source's trail could not be read.");
      }
      return data;
    },
  });
}

export type ExpectedSourceIn = components["schemas"]["ExpectedSourceIn"];

/** Declare a stream the census should expect — **B-23's last piece of onboarding**.
 *
 * The row the whole census rests on: a total is a sum over what arrived, and a sum over what
 * arrived is indistinguishable from a complete inventory. Until this existed every expectation in
 * every workspace came from the seeder.
 */
export function useDeclareExpected(periodStart: string, periodEnd: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ExpectedSourceIn): Promise<SourceCoverage> => {
      const { data, error, response } = await api.POST("/api/v1/sources", {
        params: { query: { period_start: periodStart, period_end: periodEnd } },
        body: input,
      });
      if (error || !data) {
        throw refusalFrom(response, error, "That expected source could not be declared.");
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: sourceKeys.coverage(periodStart, periodEnd),
      });
    },
  });
}

export function useDeclareNil(periodStart: string, periodEnd: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      sourceId: string;
      period_start: string;
      period_end: string;
      reason: string;
    }): Promise<SourceCoverage> => {
      const { data, error, response } = await api.POST("/api/v1/sources/{source_id}/nil", {
        params: { path: { source_id: input.sourceId } },
        body: {
          period_start: input.period_start,
          period_end: input.period_end,
          reason: input.reason,
        },
      });
      if (error || !data) {
        throw refusalFrom(response, error, "That confirmation could not be recorded.");
      }
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: sourceKeys.coverage(periodStart, periodEnd),
      });
    },
  });
}

export type OwnerIn = components["schemas"]["OwnerIn"];

/** Name — or un-name — the person accountable for this source arriving. **COM-02.**
 *
 * **`null` is a value and not an omission.** A supply whose owner has left belongs back on the
 * onboarding list, and a route that refused the unassignment until a replacement was named would
 * keep a name on a source that is no longer true. That is the version which reports 100% owned for
 * ever, so the screen offers *remove* as an option rather than hiding it behind a replacement.
 *
 * **The count is the server's.** `completeness.unowned` is computed over sources by the census, so
 * this invalidates the coverage query rather than patching the row it just changed. A locally
 * decremented count is a second opinion about what the server thinks, and the second copy is the
 * one nobody updates.
 */
export function useAssignOwner(periodStart: string, periodEnd: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      sourceId: string;
      ownerUserId: string | null;
    }): Promise<SourceCoverage> => {
      const { data, error, response } = await api.POST("/api/v1/sources/{source_id}/owner", {
        params: {
          path: { source_id: input.sourceId },
          query: { period_start: periodStart, period_end: periodEnd },
        },
        body: { owner_user_id: input.ownerUserId },
      });
      if (error || !data) {
        // The server's own sentence survives: *"that person is not in this organisation, so they
        // cannot be accountable for one of its sources"* names the fact to fix, where "could not
        // be saved" would send somebody at the network.
        throw refusalFrom(response, error, "That owner could not be recorded.");
      }
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: sourceKeys.coverage(periodStart, periodEnd),
      });
    },
  });
}

export function useRetireSource(periodStart: string, periodEnd: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      sourceId: string;
      effective_from: string;
      reason: string;
    }): Promise<SourceCoverage> => {
      const { data, error, response } = await api.POST("/api/v1/sources/{source_id}/retire", {
        params: { path: { source_id: input.sourceId } },
        body: { effective_from: input.effective_from, reason: input.reason },
      });
      if (error || !data) {
        throw refusalFrom(response, error, "That source could not be retired.");
      }
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: sourceKeys.coverage(periodStart, periodEnd),
      });
    },
  });
}

/* ------------------------------------------------------------------------------------------------
 * A nil that can be taken back (ADR-0064)
 * ---------------------------------------------------------------------------------------------- */

export type NilTrail = components["schemas"]["NilTrailOut"];
export type NilTrailEntry = components["schemas"]["NilTrailEntryOut"];

/** Say that a declared nil was wrong — **and why**, which is its own sentence.
 *
 * Corrected by supersession and never by an edit: the declaration keeps its words, author and time,
 * and the window goes back to whatever the census now computes. **That is `missing` more often than
 * not, and it is not a nil of zero** — which is why the response is the source's coverage and the
 * screen invalidates the census rather than patching a cell: a locally-edited window is a second
 * opinion about what the server thinks.
 *
 * `reason` is required by the server and by the form. It is deliberately *not* defaulted from the
 * declaration's own words: that answered *"why is there no gas?"*, and this answers *"why was that
 * wrong?"*.
 */
export function useWithdrawNil(periodStart: string, periodEnd: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      sourceId: string;
      declarationId: string;
      reason: string;
    }): Promise<SourceCoverage> => {
      const { data, error, response } = await api.POST(
        "/api/v1/sources/{source_id}/nil/{declaration_id}/withdraw",
        {
          params: {
            path: { source_id: input.sourceId, declaration_id: input.declarationId },
            query: { period_start: periodStart, period_end: periodEnd },
          },
          body: { reason: input.reason },
        },
      );
      if (error || !data) {
        throw refusalFrom(response, error, "That declaration could not be withdrawn.");
      }
      return data;
    },
    onSuccess: async () => {
      // The whole `sources` family: the trail and the links hang off the same key.
      await queryClient.invalidateQueries({ queryKey: ["sources"] });
    },
  });
}

/** Every declaration this source has had — **withdrawn ones included**.
 *
 * Fetched when a reader expands a source (`enabled`), for the reason the links are: thirty sources
 * would be thirty queries on a screen whose job is a single comparison.
 */
export function useNilTrail(
  sourceId: string | null,
  periodStart: string,
  periodEnd: string,
) {
  return useQuery({
    queryKey: [...sourceKeys.coverage(periodStart, periodEnd), "nil-trail", sourceId],
    enabled: sourceId !== null,
    queryFn: async (): Promise<NilTrail> => {
      const { data, error, response } = await api.GET("/api/v1/sources/{source_id}/nil", {
        params: {
          path: { source_id: sourceId as string },
          query: { period_start: periodStart, period_end: periodEnd },
        },
      });
      if (error || !data) {
        throw refusalFrom(response, error, "This source's declarations could not be read.");
      }
      return data;
    },
  });
}

/* ------------------------------------------------------------------------------------------------
 * Who bills a meter (ADR-0062)
 * ---------------------------------------------------------------------------------------------- */

export type Registers = components["schemas"]["RegistersOut"];
export type SupplyAccount = components["schemas"]["SupplyAccountOut"];
export type RegisterVendor = components["schemas"]["VendorOut"];
export type RegisterMeter = components["schemas"]["RegisterMeterOut"];
export type SourceSupply = components["schemas"]["SupplyOut"];
export type StreamOptions = components["schemas"]["StreamOptionsOut"];

export const registerKeys = {
  all: ["sources", "registers"] as const,
};

/** Vendors, accounts with the meters beneath them, and the meters nobody has accounted for.
 *
 * **Thrown, never defaulted to empty.** A failed read rendered as an empty register would say that
 * every meter is accounted for — the opposite of what the list exists to show, and the defect nine
 * read hooks here once had.
 */
export function useRegisters(enabled = true) {
  return useQuery({
    queryKey: registerKeys.all,
    enabled,
    queryFn: async (): Promise<Registers> => {
      const { data, error, response } = await api.GET("/api/v1/sources/registers", {});
      if (error || !data) {
        throw refusalFrom(response, error, "The register of who bills what could not be read.");
      }
      return data;
    },
  });
}

/** What can be expected at a site, **from the factor library** rather than from a typed list.
 *
 * `enabled` on a site: with none chosen there is nothing to ask. The server refuses a site in
 * another workspace, and a jurisdiction the library has no factors for, with the sentence a person
 * can act on — so a failure here is left to the caller to show rather than swallowed into an
 * empty menu (an empty menu reads as "nothing can be expected here").
 */
export function useStreamOptions(facilityId: string | null) {
  return useQuery({
    queryKey: ["sources", "options", facilityId] as const,
    enabled: facilityId !== null && facilityId !== "",
    retry: false,
    queryFn: async (): Promise<StreamOptions> => {
      const { data, error, response } = await api.GET("/api/v1/sources/options", {
        params: { query: { facility_id: facilityId as string } },
      });
      if (error || !data) {
        throw refusalFrom(response, error, "What this site can be expected to send could not be read.");
      }
      return data;
    },
  });
}

/** After any register write, **both** the register and the census are stale: an account's dates
 * narrow what the census expects, so a patched register beside an unchanged census would be the
 * two disagreeing about the same fact. */
function useRegisterWrite<I, O>(
  write: (input: I) => Promise<O>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["sources"] });
      // The Vendors table's numbers (`GET /sources/vendors/summary`) are read through the same rows.
      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
    },
  });
}

export function useRecordVendor() {
  return useRegisterWrite(async (input: { name: string }): Promise<RegisterVendor> => {
    const { data, error, response } = await api.POST("/api/v1/sources/vendors", { body: input });
    if (error || !data) throw refusalFrom(response, error, "That vendor could not be recorded.");
    return data;
  });
}

/** Correct how a vendor is written. Only the name changes: accounts, meters and figures stay.
 *
 * `expectedVersion` is the version of the vendor the drawer was opened with (G-P9-04): the server
 * refuses with 409 `stale_version` if someone else changed it since, so a rename made from an old
 * view is not silently the last writer. The same on retire and restore below. */
export function useRenameVendor() {
  return useRegisterWrite(
    async (input: { id: string; name: string; expectedVersion?: number }): Promise<RegisterVendor> => {
      const { data, error, response } = await api.PUT("/api/v1/sources/vendors/{vendor_id}", {
        params: { path: { vendor_id: input.id } },
        body: { name: input.name, expected_version: input.expectedVersion },
      });
      if (error || !data) throw refusalFrom(response, error, "That vendor could not be renamed.");
      return data;
    },
  );
}

/** Retire a vendor: never a delete. It keeps its history and figures and takes no new account. */
export function useRetireVendor() {
  return useRegisterWrite(async (input: { id: string; expectedVersion?: number }): Promise<RegisterVendor> => {
    const { data, error, response } = await api.POST("/api/v1/sources/vendors/{vendor_id}/retire", {
      params: { path: { vendor_id: input.id } },
      body: { expected_version: input.expectedVersion },
    });
    if (error || !data) throw refusalFrom(response, error, "That vendor could not be retired.");
    return data;
  });
}

export function useRestoreVendor() {
  return useRegisterWrite(async (input: { id: string; expectedVersion?: number }): Promise<RegisterVendor> => {
    const { data, error, response } = await api.POST("/api/v1/sources/vendors/{vendor_id}/restore", {
      params: { path: { vendor_id: input.id } },
      body: { expected_version: input.expectedVersion },
    });
    if (error || !data) throw refusalFrom(response, error, "That vendor could not be restored.");
    return data;
  });
}

export function useRecordAccount() {
  return useRegisterWrite(
    async (input: components["schemas"]["SupplyAccountIn"]): Promise<SupplyAccount> => {
      const { data, error, response } = await api.POST("/api/v1/sources/accounts", {
        body: input,
      });
      if (error || !data) throw refusalFrom(response, error, "That account could not be recorded.");
      return data;
    },
  );
}

/** End an account from a date: **one row ends every stream beneath it**, which is what the account
 * is for. The meters stay attached — the register says the account ended, not that nobody bills
 * the meter — and a successor is attached with `useAttachMeters`. */
export function useRetireAccount() {
  return useRegisterWrite(
    async (input: {
      accountId: string;
      effective_from: string;
      reason: string;
    }): Promise<SupplyAccount> => {
      const { data, error, response } = await api.POST(
        "/api/v1/sources/accounts/{account_id}/retire",
        {
          params: { path: { account_id: input.accountId } },
          body: { effective_from: input.effective_from, reason: input.reason },
        },
      );
      if (error || !data) throw refusalFrom(response, error, "That account could not be retired.");
      return data;
    },
  );
}

export function useAttachMeters() {
  return useRegisterWrite(
    async (input: { accountId: string; meterIds: string[] }): Promise<SupplyAccount> => {
      const { data, error, response } = await api.POST(
        "/api/v1/sources/accounts/{account_id}/meters",
        {
          params: { path: { account_id: input.accountId } },
          body: { meter_ids: input.meterIds },
        },
      );
      if (error || !data) throw refusalFrom(response, error, "Those meters could not be attached.");
      return data;
    },
  );
}

/** Set — or **clear** — one meter's account. `null` returns it to *not recorded*, which is a real
 * act and not an error: a mistaken attachment has to be undoable. */
export function useSetMeterAccount() {
  return useRegisterWrite(
    async (input: { meterId: string; accountId: string | null }): Promise<RegisterMeter> => {
      const { data, error, response } = await api.POST("/api/v1/sources/meters/{meter_id}/account", {
        params: { path: { meter_id: input.meterId } },
        body: { supply_account_id: input.accountId },
      });
      if (error || !data) throw refusalFrom(response, error, "That meter could not be updated.");
      return data;
    },
  );
}
